import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { endOfDay, getDaysInMonth, isSameDay, startOfDay, startOfMonth } from 'date-fns';
import { weekDays } from '../../constants/week-days';
import { CalendarDay } from '../../models/calendar-day';
import { IDateRange } from '../../models/date-range';

@Component({
    selector: 'calendar-days-grid',
    template: `
        <div class="week-days-grid">
            <span *ngFor="let weekDay of weekDays" class="week-day-label">
                {{ weekDay.shortLabel }}
            </span>
        </div>
        <div class="calendar-grid">
            <ng-container *ngFor="let day of days; let i = index; trackBy: dayTrackByFn">
                <calendar-day
                    [date]="day.date"
                    [selected]="getIsSelected(day)"
                    [isStartOfRange]="getIsStartOfRange(day)"
                    [withinRange]="getIsWithinRange(day)"
                    [isEndOfRange]="getIsEndOfRange(day)"
                    [disabled]="getIsDisabled(day)"
                    [muted]="getIsMuted(day, i)"
                    [hidden]="getIsHidden(day, i)"
                    [isToday]="getIsToday(day)"
                    [rangeHovered]="getIsRangeHovered(day)"
                    [isRangeHoveredStart]="getIsRangeHoveredStart(day)"
                    [isRangeHoveredEnd]="getIsRangeHoveredEnd(day)"
                    [isFirstDayOnCalendar]="getIsFirstDayOnCalendar(day, i)"
                    [isLastDayOnCalendar]="getIsLastDayOnCalendar(day, i)"
                    (dayClick)="handleDayClick(day)"
                    (dayMouseEnter)="handleDayMouseEnter(day)"
                >
                </calendar-day>
            </ng-container>
        </div>
    `,
    styleUrls: ['./calendar-days-grid.component.scss']
})
export class CalendarDaysGridComponent implements OnChanges {
    @Input() public date: Date;
    @Input() public dateRange: IDateRange;
    @Input() public rangeHovered: IDateRange;
    @Input() public month: Date;
    @Input() public lowerLimit: Date;
    @Input() public upperLimit: Date;
    @Input() public hidePreviousMonth = false;
    @Input() public hideNextMonth = false;
    @Input() public isSettingDateRangeStart = false;
    @Input() public isSettingDateRangeEnd = false;
    @Output() public dateChange = new EventEmitter<Date>();
    @Output() public dateHoveredChange = new EventEmitter<Date>();

    public days: CalendarDay[] = [];
    public weekDays = weekDays;
    private _now = new Date();
    private _hoveredDay: CalendarDay = null;
    private _daysInFiveWeeks = 7 * 5;
    private _daysInSixWeeks = 7 * 6;
    private _gridCount = this._daysInSixWeeks;
    private _indexOfStartOfMonth = -1;
    private _monthLength = 0;
    private _numberOfNextMonthDaysShowing = 0;
    private _buildingHiddenLastRow = false;

    // Cache.
    private _isTodayCache = new Map<CalendarDay, boolean>();
    private _isSelectedCache = new Map<CalendarDay, boolean>();
    private _isStartOfRangeCache = new Map<CalendarDay, boolean>();
    private _isEndOfRangeCache = new Map<CalendarDay, boolean>();
    private _isWithinRangeCache = new Map<CalendarDay, boolean>();
    private _isRangeHoveredCache = new Map<CalendarDay, boolean>();
    private _isRangeHoveredStartCache = new Map<CalendarDay, boolean>();
    private _isRangeHoveredEndCache = new Map<CalendarDay, boolean>();
    private _isHiddenCache = new Map<CalendarDay, boolean>();
    private _isMutedCache = new Map<CalendarDay, boolean>();
    private _isDisabledCache = new Map<CalendarDay, boolean>();
    private _isLastDayOnCalendarCache = new Map<CalendarDay, boolean>();
    private _isFirstDayOnCalendarCache = new Map<CalendarDay, boolean>();

    // Lifecycle hooks.
    public ngOnChanges(simpleChanges: SimpleChanges): void {
        if (
            !!simpleChanges.month ||
            // This would mean that the DatePickerType has changed.
            (!!simpleChanges.date && !!simpleChanges.dateRange)
        ) {
            this.initMonth();
        }

        // If something important changes, clear the cache.
        if (
            !!simpleChanges.month ||
            !!simpleChanges.date ||
            !!simpleChanges.dateRange ||
            !!simpleChanges.rangeHovered
        ) {
            this._hoveredDay = null;
            this._isTodayCache.clear();
            this._isSelectedCache.clear();
            this._isStartOfRangeCache.clear();
            this._isEndOfRangeCache.clear();
            this._isWithinRangeCache.clear();
            this._isRangeHoveredCache.clear();
            this._isRangeHoveredStartCache.clear();
            this._isRangeHoveredEndCache.clear();
            this._isHiddenCache.clear();
            this._isMutedCache.clear();
            this._isDisabledCache.clear();
            this._isLastDayOnCalendarCache.clear();
            this._isFirstDayOnCalendarCache.clear();
        }
    }

    public initMonth(): void {
        const monthStart: Date = startOfMonth(this.month);
        const year: number = monthStart.getFullYear();
        const zeroBasedMonth: number = monthStart.getMonth();

        this._numberOfNextMonthDaysShowing = 0;
        this._buildingHiddenLastRow = false;
        this._monthLength = getDaysInMonth(monthStart);
        this._indexOfStartOfMonth = monthStart.getDay();

        if (!this.month) {
            return;
        }

        for (let dayIndex = 0; dayIndex < this._gridCount; dayIndex++) {
            let day: CalendarDay;

            if (!this.days[dayIndex]) {
                day = new CalendarDay();
                this.days.push(day);
            } else {
                day = this.days[dayIndex];
            }

            // Reset the date.
            day.date = new Date(year, zeroBasedMonth, this.getDayOfMonth(dayIndex));

            // Next month days.
            if (this.getDayOfMonth(dayIndex) > this._monthLength) {
                this._numberOfNextMonthDaysShowing++;

                // Changing the below <= to < makes it so we see the next month days if the
                // last day of `this.month` is a Saturday.
                if (this._numberOfNextMonthDaysShowing === 1 && dayIndex <= this._daysInFiveWeeks) {
                    this._buildingHiddenLastRow = true;
                }
            }
        }
    }

    // Accessors for boolean inputs.
    public getDayOfMonth(dayIndex: number): number {
        return dayIndex - this._indexOfStartOfMonth + 1;
    }

    public getIsToday(day: CalendarDay): boolean {
        if (!this._isTodayCache.has(day)) {
            this._isTodayCache.set(day, isSameDay(this._now, day.date));
        }
        return this._isTodayCache.get(day);
    }

    public getIsSelected(day: CalendarDay): boolean {
        if (!this._isSelectedCache.has(day)) {
            if (!!this.date) {
                this._isSelectedCache.set(day, isSameDay(this.date, day.date));
            } else if (!!this.dateRange && !!this.dateRange.start) {
                this._isSelectedCache.set(
                    day,
                    isSameDay(this.dateRange.start, day.date) ||
                        isSameDay(this.dateRange.end, day.date)
                );
            } else {
                this._isSelectedCache.set(day, false);
            }
        }
        return this._isSelectedCache.get(day);
    }

    public getIsStartOfRange(day: CalendarDay): boolean {
        if (!this.dateRange) {
            return false;
        }
        if (!this._isStartOfRangeCache.has(day)) {
            this._isStartOfRangeCache.set(day, isSameDay(this.dateRange.start, day.date));
        }
        return this._isStartOfRangeCache.get(day);
    }

    public getIsEndOfRange(day: CalendarDay): boolean {
        if (!this.dateRange) {
            return false;
        }
        if (!this._isEndOfRangeCache.has(day)) {
            this._isEndOfRangeCache.set(day, isSameDay(this.dateRange.end, day.date));
        }
        return this._isEndOfRangeCache.get(day);
    }

    public getIsWithinRange(day: CalendarDay): boolean {
        if (!this.dateRange) {
            return false;
        }
        if (!this._isWithinRangeCache.has(day)) {
            this._isWithinRangeCache.set(
                day,
                startOfDay(day.date) > this.dateRange.start &&
                    endOfDay(day.date) < this.dateRange.end
            );
        }
        return this._isWithinRangeCache.get(day);
    }

    public getIsRangeHovered(day: CalendarDay): boolean {
        if (!this.dateRange) {
            return false;
        }
        if (!this._isRangeHoveredCache.has(day)) {
            this._isRangeHoveredCache.set(
                day,
                day.date >= this.rangeHovered.start && day.date <= this.rangeHovered.end
            );
        }
        return this._isRangeHoveredCache.get(day);
    }

    public getIsRangeHoveredStart(day: CalendarDay): boolean {
        if (!this.rangeHovered) {
            return false;
        }
        if (!this._isRangeHoveredStartCache.has(day)) {
            this._isRangeHoveredStartCache.set(day, isSameDay(day.date, this.rangeHovered.start));
        }
        return this._isRangeHoveredStartCache.get(day);
    }

    public getIsRangeHoveredEnd(day: CalendarDay): boolean {
        if (!this.rangeHovered) {
            return false;
        }
        if (!this._isRangeHoveredEndCache.has(day)) {
            this._isRangeHoveredEndCache.set(day, isSameDay(day.date, this.rangeHovered.end));
        }
        return this._isRangeHoveredEndCache.get(day);
    }

    public getIsMuted(day: CalendarDay, dayIndex: number): boolean {
        if (!this._isMutedCache.has(day)) {
            const mute =
                (!this.hidePreviousMonth && dayIndex < this._indexOfStartOfMonth) ||
                (!this.hideNextMonth && this.getDayOfMonth(dayIndex) > this._monthLength);
            this._isMutedCache.set(day, mute);
        }
        return this._isMutedCache.get(day);
    }

    public getIsHidden(day: CalendarDay, dayIndex: number): boolean {
        if (!this._isHiddenCache.has(day)) {
            const hide =
                (this.hidePreviousMonth && dayIndex < this._indexOfStartOfMonth) ||
                (this.hideNextMonth && this.getDayOfMonth(dayIndex) > this._monthLength) ||
                (this._buildingHiddenLastRow && dayIndex >= this._daysInFiveWeeks);
            this._isHiddenCache.set(day, hide);
        }
        return this._isHiddenCache.get(day);
    }

    public getIsDisabled(day: CalendarDay): boolean {
        if (!this._isDisabledCache.has(day)) {
            this._isDisabledCache.set(
                day,
                (!!this.lowerLimit && day.date < this.lowerLimit) ||
                    (!!this.upperLimit && day.date > this.upperLimit)
            );
        }
        return this._isDisabledCache.get(day);
    }

    public getIsLastDayOnCalendar(day: CalendarDay, dayIndex: number): boolean {
        if (!this._isLastDayOnCalendarCache.has(day)) {
            this._isLastDayOnCalendarCache.set(
                day,
                this.hideNextMonth && this.getDayOfMonth(dayIndex) === this._monthLength
            );
        }
        return this._isLastDayOnCalendarCache.get(day);
    }

    public getIsFirstDayOnCalendar(day: CalendarDay, dayIndex: number): boolean {
        if (!this._isFirstDayOnCalendarCache.has(day)) {
            this._isFirstDayOnCalendarCache.set(
                day,
                this.hidePreviousMonth && this.getDayOfMonth(dayIndex) === 1
            );
        }
        return this._isFirstDayOnCalendarCache.get(day);
    }

    // Event handlers.
    public handleDayClick(day: CalendarDay): void {
        this.dateChange.emit(day.date);
    }

    public handleDayMouseEnter(day: CalendarDay): void {
        if (!day || day.disabled || day.hidden) {
            return;
        }
        if (!this._hoveredDay || this._hoveredDay.date !== day.date) {
            this._hoveredDay = day;
            this.dateHoveredChange.emit(this._hoveredDay.date);
        }
    }

    // Template optimization.
    public dayTrackByFn(index: number, day: CalendarDay): Date {
        return day.date;
    }
}
